import { useMemo } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { priorityLabel } from '../constants';
import { workItemRef } from '../paths';
import { statesHooks, useWorkItems } from '../queries';
import { formatEstimate } from '../timeFormat';
import type { Project, State, WorkItem } from '../types';
import { useSelectedWorkItem } from '../useSelectedWorkItem';
import { AssigneeAvatars } from './AssigneeAvatars';
import { filterParams, type WorkItemFilters } from './workItemFilters';
import { DEFAULT_ORDER, sortWorkItems, type WorkItemOrder } from './workItemOrder';
import { WorkItemDetailDrawer } from './WorkItemDetailDrawer';
import { WorkItemTypeIcon } from './WorkItemTypeIcon';

const PRIORITY_DOT: Record<string, string> = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-amber-400',
  low: 'bg-sky-400',
  none: 'bg-muted-foreground/30',
};

interface RowsProps {
  project: Project;
  mode: 'list' | 'table';
  filters?: WorkItemFilters;
  order?: WorkItemOrder;
}

export function WorkItemsRows({ project, mode, filters, order = DEFAULT_ORDER }: RowsProps) {
  const statesQuery = statesHooks.useList({ project: project.id });
  const itemsQuery = useWorkItems({ project: project.id, page_size: 200, ...filterParams(filters) });
  const [selected, setSelected] = useSelectedWorkItem();

  const statesById = useMemo(() => {
    const raw = statesQuery.data;
    const list = (Array.isArray(raw) ? raw : (raw?.results ?? [])) as State[];
    return new Map(list.map((s) => [s.id, s]));
  }, [statesQuery.data]);

  const states = useMemo(
    () => [...statesById.values()].sort((a, b) => a.order - b.order),
    [statesById],
  );

  if (statesQuery.isPending || itemsQuery.isPending) {
    return <Skeleton className="h-64 w-full" />;
  }

  const items = sortWorkItems(itemsQuery.data?.results ?? [], order);

  if (items.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-10 text-center">
        <p className="text-muted-foreground text-sm">No work items match.</p>
      </div>
    );
  }

  return (
    <>
      {mode === 'table' ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-28">ID</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Assignees</TableHead>
              <TableHead>Due</TableHead>
              <TableHead className="text-right">Estimate</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const state = item.state ? statesById.get(item.state) : undefined;
              return (
                <TableRow
                  key={item.id}
                  className="cursor-pointer"
                  onClick={() => setSelected(workItemRef(item))}
                >
                  <TableCell className="text-muted-foreground font-mono text-xs">
                    <span className="flex items-center gap-1.5">
                      <WorkItemTypeIcon type={item.type_detail} className="size-3.5" />
                      {item.identifier}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium">{item.title}</TableCell>
                  <TableCell>
                    {state ? (
                      <span className="flex items-center gap-1.5 text-sm">
                        <span
                          className="size-2 rounded-full"
                          style={{ backgroundColor: state.color || '#94a3b8' }}
                        />
                        {state.name}
                      </span>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    {item.priority !== 'none' ? (
                      <span className="flex items-center gap-1.5 text-sm">
                        <span className={cn('size-2 rounded-full', PRIORITY_DOT[item.priority])} />
                        {priorityLabel(item.priority)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    {item.assignee_details && item.assignee_details.length > 0 ? (
                      <AssigneeAvatars assignees={item.assignee_details} />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{item.due_date ?? '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatEstimate(item.estimate, project.estimate_type === 'hours') || '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <div className="space-y-5">
          {states.map((state) => {
            const group = items.filter((i) => i.state === state.id);
            if (group.length === 0) return null;
            return (
              <section key={state.id}>
                <div className="mb-1.5 flex items-center gap-2">
                  <span
                    className="size-2.5 rounded-full"
                    style={{ backgroundColor: state.color || '#94a3b8' }}
                  />
                  <span className="text-sm font-medium">{state.name}</span>
                  <span className="text-muted-foreground text-xs">{group.length}</span>
                </div>
                <ul className="divide-y rounded-md border">
                  {group.map((item: WorkItem) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => setSelected(workItemRef(item))}
                        className="hover:bg-muted/50 flex w-full items-center gap-3 px-3 py-2 text-left"
                      >
                        <WorkItemTypeIcon type={item.type_detail} className="size-3.5" />
                        <span className="text-muted-foreground font-mono text-xs">
                          {item.identifier}
                        </span>
                        <span className="flex-1 truncate text-sm">{item.title}</span>
                        {item.priority !== 'none' ? (
                          <span className={cn('size-2 rounded-full', PRIORITY_DOT[item.priority])} />
                        ) : null}
                        {item.due_date ? (
                          <span className="text-muted-foreground text-xs">{item.due_date}</span>
                        ) : null}
                        <AssigneeAvatars assignees={item.assignee_details} />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      <WorkItemDetailDrawer project={project} workItemId={selected} onClose={() => setSelected(null)} />
    </>
  );
}
